import { useEffect, useState } from "react";
import { Modal, ModalBody, ModalHeader, ModalContent } from "@heroui/react";
import {
  EditableSelectComponent,
  renderSection,
} from "components";
import { AlertInfo, Checkmark, Edit, Wrench } from "assets/icons";
import ButtonNew from "components/buttonNew/buttonNew";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import SearchComponent, {
  SearchMode,
} from "shared/components/searchBar/components/searchComponent";
import { ResourceType } from "shared/types/resource";
import { ContactTags } from "shared/types/contacts";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css"; // Base styling

// Type assertion for React 18 compatibility
const DatePicker = ReactDatePicker as any;
import {
  FacadeDto,
  AdjacentToPublicDomain,
  BuildingParticleStatus,
  FacadePosition,
  FinishType,
  InsulatedStatus,
  SinglePlane,
} from "../../../../shared/types/facade";
import { FileLabel } from "shared/types/file";
import { ImagesContainer } from "shared/components/imagesContainer/imagesContainer";

interface ModalFacadeProps {
  isOpen: boolean;
  onClose: () => void;
  building: any;
  onFacadeAdded: () => void;
  facade: any;
}

const ModalFacade = ({
  isOpen,
  onClose,
  building,
  onFacadeAdded,
  facade,
}: ModalFacadeProps) => {
  const [facadeData, setFacadeData] = useState<FacadeDto>({} as FacadeDto);
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { session } = useSession();

  useEffect(() => {
    setFacadeData(facade || ({} as FacadeDto));
    setEditMode(!facade);
    setIsLoading(false);
  }, [facade]);

  // Function to handle changes in input/select fields
  const handleChange = (key: keyof FacadeDto, value: any) => {
    setFacadeData((prevState: any) => ({
      ...prevState,
      [key]: value,
    }));
  };

  // Define the shared properties for the roof modal
  const sharedFacadeProps = {
    editMode: editMode,
    isLoading: isLoading,
    onEditClick: () => {
      /* ... */
    },
    onSaveClick: () => {
      console.log("Saved data:", facadeData);
      onClose();
    },
    originalState: facadeData,
    editedState: facadeData,
    setStateFunction: setFacadeData,
  };

  const createContactFieldComponent = (
    label: any,
    key: any,
    resourceFilter: any,
    metadataFilter: any,
  ) => {
    return () => (
      <div className={editMode ? "grow p-2" : ""}>
        <SearchComponent
          type="regular"
          label={label}
          resourceFilter={resourceFilter}
          metadataFilter={metadataFilter}
          onSelect={(resource) =>
            setFacadeData((prev: any) => ({
              ...prev,
              [key]: {
                resourceId: resource.resourceId,
                name: resource.name,
                type: resource.type,
              },
            }))
          }
          initialValue={
            (facadeData as any)?.[key] ? (facadeData as any)?.[key].name : null
          }
          mode={SearchMode.SEARCH_AND_PRESENT}
          editMode={editMode}
        />
      </div>
    );
  };

  const facadeFieldsConfig = [
    {
      label: "Status",
      key: "status",
      Component: () => (
        <EditableSelectComponent
          label="Status"
          editMode={editMode}
          value={facadeData.status || ""}
          options={Object.values(BuildingParticleStatus).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("status", value)}
        />
      ),
    },
    {
      label: "Positie",
      key: "facadePosition",
      Component: () => (
        <EditableSelectComponent
          label="Positie"
          editMode={editMode}
          value={facadeData.facadePosition || ""}
          options={Object.values(FacadePosition).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("facadePosition", value)}
        />
      ),
    },
    {
      label: "Gevel in één vlak",
      key: "singlePlane",
      Component: () => (
        <EditableSelectComponent
          label="Gevel in één vlak"
          editMode={editMode}
          value={facadeData.singlePlane || ""}
          options={Object.values(SinglePlane).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("singlePlane", value)}
        />
      ),
    },
    {
      label: "Oppervlakte bruto (m²)",
      key: "grossSurfaceArea",
    },
    {
      label: "Kroonlijsthoogte (m)",
      key: "corniceHeight",
    },
    {
      label: "Gevelafwerking",
      key: "finishType",
      Component: () => (
        <EditableSelectComponent
          label="Gevelafwerking"
          editMode={editMode}
          value={facadeData.finishType || ""}
          options={Object.values(FinishType).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("finishType", value)}
        />
      ),
    },
    {
      label: "Geïsoleerd",
      key: "insulated",
      Component: () => (
        <EditableSelectComponent
          label="Geïsoleerd"
          editMode={editMode}
          value={facadeData.insulated || ""}
          options={Object.values(InsulatedStatus).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("insulated", value)}
        />
      ),
    },
    {
      label: "Spouw dikte (mm)",
      key: "cavityThickness",
    },
    {
      label: "Grenzend aan openbaar domein",
      key: "adjacentToPublicDomain",
      Component: () => (
        <EditableSelectComponent
          label="Grenzend aan openbaar domein"
          editMode={editMode}
          value={facadeData.adjacentToPublicDomain || ""}
          options={Object.values(AdjacentToPublicDomain).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("adjacentToPublicDomain", value)}
        />
      ),
    },
  ];

  const facadeExtraFieldsConfig = [
    {
      label: "Installateur",
      key: "installer",
      Component: createContactFieldComponent(
        "Zoek de installateur in de lijst van installateurs",
        "installer",
        [ResourceType.COMPANY, ResourceType.CONTACT],
        ContactTags.AannemerRuwbouw,
      ),
    },
    {
      label: "Opleveringsdatum",
      key: "deliveryDate",
      Component: () => (
        <DatePicker
          onChange={(date: any) =>
            setFacadeData((prev: any) => ({
              ...prev,
              deliveryDate: date,
            }))
          }
          selected={
            facadeData.deliveryDate ? new Date(facadeData.deliveryDate) : null
          }
          dateFormat="yyyy/MM/dd"
          className="w-full rounded-sm bg-white p-2.5 text-neutral-primary shadow-form-element placeholder:text-neutral-secondary-mid focus:ring-3 focus:ring-opacity-50"
        />
      ),
    },
    {
      label: "Volgende inspectiedatum",
      key: "nextInspectionDate",
      Component: () => (
        <DatePicker
          onChange={(date: any) =>
            setFacadeData((prev: any) => ({
              ...prev,
              nextInspectionDate: date,
            }))
          }
          selected={
            facadeData.nextInspectionDate
              ? new Date(facadeData.nextInspectionDate)
              : null
          }
          dateFormat="yyyy/MM/dd"
          className="w-full rounded-sm bg-white p-2.5 text-neutral-primary shadow-form-element placeholder:text-neutral-secondary-mid focus:ring-3 focus:ring-opacity-50"
        />
      ),
    },
  ];

  const handleSaveChanges = async () => {
    if (!facadeData.facadePosition) {
      return toast.error("Positie is een verplicht veld");
    }

    (facadeData as any).building = { id: building.id };
    try {
      const response = await fetchWithAuth(
        `${API_URL}/building/create-facade`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(facadeData),
        },
        session,
      );

      if (response.ok) {
        toast("Facade saved successfully!");
        setFacadeData({} as FacadeDto);
        onFacadeAdded();
        setEditMode(false);
        onClose();
      } else {
        toast("Error saving facade. Please try again.");
      }
    } catch (error) {
      console.error("Failed to save facade: ", error);
      toast("Error saving facade!");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setFacadeData({} as FacadeDto);
        onClose();
      }}
      size="5xl"
      shouldBlockScroll={true}
      scrollBehavior="inside"
      backdrop="blur"
      className="p-8"
    >
      <ModalContent className="flex flex-col gap-4 font-normal text-neutral-primary">
        <ModalHeader className="flex flex-row items-center justify-between p-0 font-normal">
          {facadeData && building ? (
            <p>
              {building?.id} - {facadeData?.facadePosition}
              bewerken{" "}
            </p>
          ) : (
            <p>Nieuwe gevel toevoegen</p>
          )}
          <div className="flex gap-6">
            <ButtonNew
              variant="primaryMoss"
              onClick={() => {
                if (!editMode) setEditMode(true);
                else {
                  handleSaveChanges();
                }
              }}
              icon={editMode ? <Checkmark /> : <Edit />}
              label={editMode ? "Bewaren" : "Bewerken"}
            />
          </div>
        </ModalHeader>
        <ModalBody className="grid gap-5 gap-y-8 px-0 sm:grid-cols-2">
          <div className="flex flex-col gap-2.5">
            {renderSection({
              title: "Algemeen",
              icon: <AlertInfo />,
              fieldsConfig: facadeFieldsConfig,
              showEditToggle: false,
              ...sharedFacadeProps,
            })}
          </div>
          <div className="flex flex-col gap-2.5">
            {renderSection({
              title: "Installatie",
              icon: <Wrench />,
              fieldsConfig: facadeExtraFieldsConfig,
              showEditToggle: false,
              ...sharedFacadeProps,
            })}
          </div>
          <div className="col-span-full">
            <ImagesContainer
              buildingId={building.id}
              fileLabel={facadeData.facadePosition == FacadePosition.FRONT ? FileLabel.FrontagePicture : facadeData.facadePosition == FacadePosition.BACK ? FileLabel.BackFacadePicture : facadeData.facadePosition == FacadePosition.SIDE_LEFT ? FileLabel.SideFacadeLeftPicture : FileLabel.SideFacadeRightPicture}
              title={`Foto's van de ${facadeData.facadePosition}`}
            />
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ModalFacade;
